import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { Camera, Package, Share2, ChevronDown, ChevronUp } from 'lucide-react';
import '@aws-amplify/ui-react/styles.css';
import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandProvider, useBrand } from './context/BrandContext';
import LoginModal from './components/LoginModal';
import DesignWorkflow from './components/DesignWorkflow';
import ProductDisplay from './components/ProductDisplay';
import ProductGallery from './components/ProductGallery';
import SavedDesigns from './components/SavedDesigns';
import OrderSuccessPage from './pages/OrderSuccessPage';
import OrderCancelledPage from './pages/OrderCancelledPage';
import ComingSoonPage from './pages/ComingSoonPage';
import ProductPage from './pages/ProductPage';
import ShopPage from './pages/ShopPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FAQ from './pages/FAQ';
import ProfilePage from './pages/ProfilePage';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import ProxyRedirect from './pages/ProxyRedirect';
import AdminRoute from './components/AdminRoute';
import AdminDashboard from './components/AdminDashboard';
import { amplifyApi } from './services/amplifyApi';

// Configure Amplify
Amplify.configure(awsconfig);

// FAQ accordion item for studio page
function FaqItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-semibold text-slate-900 pr-4">{question}</span>
        {isOpen
          ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
          : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        }
      </button>
      {isOpen && (
        <div className="px-5 pb-4 text-slate-600 text-sm leading-relaxed">{answer}</div>
      )}
    </div>
  );
}

// Main app component - now accessible to everyone
function MainApp() {
  const { user, isAuthenticated, signOut, openLoginModal } = useAuth();
  const brand = useBrand();
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [showWorkflow, setShowWorkflow] = useState(true);
  const [loadedDesign, setLoadedDesign] = useState(null);
  const [preSelectedBlueprint, setPreSelectedBlueprint] = useState(null);

  const handleWorkflowComplete = (createdProducts) => {
    setProducts(createdProducts);
    setShowWorkflow(false);
    setError(null);
    setPreSelectedBlueprint(null);
  };

  const handleWorkflowError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleStartNew = () => {
    setProducts([]);
    setShowWorkflow(true);
    setError(null);
    setLoadedDesign(null);
    setPreSelectedBlueprint(null);
  };

  const handleLoadDesign = (design) => {
    setLoadedDesign(design);
    setShowWorkflow(true);
    setProducts([]);
    setError(null);
    setPreSelectedBlueprint(null);
  };

  const handleCreateSimilar = (blueprintInfo) => {
    setPreSelectedBlueprint(blueprintInfo);
    setShowWorkflow(true);
    setLoadedDesign(null);
    setProducts([]);
    setError(null);
    // Scroll to workflow section at top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearPreSelection = () => {
    setPreSelectedBlueprint(null);
  };

  const handleCheckout = async (productId) => {
    try {
      const response = await amplifyApi.initiateCheckout(productId);
      console.log('Checkout response:', response);

      if (response.success === false) {
        setError(response.error || 'Unable to proceed to checkout.');
        return;
      }

      if (response.checkoutUrl) {
        // Show message if available
        if (response.message) {
          alert(response.message);
        }

        // Open in new tab for better user experience
        window.open(response.checkoutUrl, '_blank');
      }
    } catch (err) {
      setError('Failed to initiate checkout. Please try again.');
      console.error('Checkout error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-blue-100 selection:text-blue-900">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {/* Logo Icon or Custom Image */}
            {brand.logo?.customImage ? (
              <img
                src={brand.logo.customImage}
                alt={brand.name}
                className="h-9 w-auto"
              />
            ) : (
              <div className="relative w-9 h-9">
                <div
                  className="absolute inset-0 rounded-xl rotate-3 shadow-lg"
                  style={{
                    background: `linear-gradient(to bottom right, ${brand.colors.primary}, ${brand.colors.secondary})`,
                    boxShadow: `0 10px 15px -3px ${brand.colors.primary}40`
                  }}
                ></div>
                <div
                  className="absolute inset-0 rounded-xl flex items-center justify-center"
                  style={{
                    background: `linear-gradient(to bottom right, ${brand.colors.primaryHover}, ${brand.colors.secondary})`
                  }}
                >
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                </div>
              </div>
            )}
            {/* Logo Text */}
            <div className="flex flex-col">
              <h1 className="text-xl font-bold tracking-tight text-slate-900">
                {brand.nameParts.main}<span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${brand.colors.primary}, ${brand.colors.secondary})`
                  }}
                >{brand.nameParts.accent}</span>
              </h1>
              <span className="text-[10px] font-medium text-slate-400 -mt-1 tracking-wide">{brand.tagline}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link
                  to="/profile"
                  className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  My Account
                </Link>
                <button
                  onClick={signOut}
                  className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => openLoginModal()}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:shadow-md"
                style={{
                  backgroundImage: `linear-gradient(to right, ${brand.colors.primary}, ${brand.colors.secondary})`
                }}
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto text-center space-y-4">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 border rounded-full text-sm font-medium mb-2"
            style={{
              backgroundColor: `${brand.colors.primary}10`,
              borderColor: `${brand.colors.primary}30`,
              color: brand.colors.primary
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ backgroundColor: brand.colors.primary }}
            ></span>
            {brand.features?.studioHero?.badge || 'Premium Merchandise'}
          </div>
          <h2 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            {(() => {
              const hero = brand.features?.studioHero;
              if (!hero?.headline) {
                return <>Turn your designs into <span style={{ color: brand.colors.primary }}>real products</span>.</>;
              }
              const accent = hero.accentText;
              if (!accent || !hero.headline.includes(accent)) {
                return hero.headline;
              }
              const idx = hero.headline.indexOf(accent);
              return (
                <>
                  {hero.headline.slice(0, idx)}
                  <span style={{ color: brand.colors.primary }}>{accent}</span>
                  {hero.headline.slice(idx + accent.length)}
                </>
              );
            })()}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {brand.features?.studioHero?.subtitle || 'Upload your artwork, choose your products, and create premium merchandise instantly.'}
          </p>
        </section>

        {/* How It Works - Process Steps */}
        {brand.features?.processSteps?.enabled && brand.features.processSteps.steps?.length > 0 && (
          <section className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {brand.features.processSteps.steps.map((step, i) => {
                const StepIcon = step.icon === 'camera' ? Camera : step.icon === 'share' ? Share2 : Package;
                return (
                  <div key={i} className="text-center p-6 rounded-xl bg-white border border-slate-200 shadow-sm">
                    <div
                      className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                      style={{ backgroundColor: `${brand.colors.primary}12` }}
                    >
                      <StepIcon className="w-6 h-6" style={{ color: brand.colors.primary }} />
                    </div>
                    <div
                      className="text-xs font-bold uppercase tracking-wider mb-1"
                      style={{ color: brand.colors.primary }}
                    >
                      Step {step.number || i + 1}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-slate-500">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Error Messages */}
        {error && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-700 animate-slide-up">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
        )}

        {/* Saved Designs */}
        <section className="max-w-4xl mx-auto">
          <SavedDesigns onLoadDesign={handleLoadDesign} />
        </section>

        {/* Design Workflow */}
        {showWorkflow && (
          <section className="max-w-4xl mx-auto">
            <DesignWorkflow
              onComplete={handleWorkflowComplete}
              onError={handleWorkflowError}
              initialDesign={loadedDesign}
              preSelectedBlueprint={preSelectedBlueprint}
              onClearPreSelection={handleClearPreSelection}
            />
          </section>
        )}

        {/* Results Section */}
        {!showWorkflow && products.length > 0 && (
          <section className="animate-fade-in space-y-6">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-2xl font-bold text-slate-900">Your Products</h3>
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">{products.length} Items Created</span>
                <button
                  onClick={handleStartNew}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  + Create More
                </button>
              </div>
            </div>
            <ProductDisplay
              products={products}
              onCheckout={handleCheckout}
            />
          </section>
        )}

        <div className="border-t border-slate-200"></div>

        {/* Store Products Section */}
        <section>
          <ProductGallery
            api={amplifyApi}
            user={user}
            onCheckout={handleCheckout}
            onCreateSimilar={handleCreateSimilar}
          />
        </section>
      </main>

      {/* FAQ Section */}
      {brand.features?.studioFaq?.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {brand.features.studioFaq.map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </section>
      )}

      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="text-center md:text-left">
              <p className="text-slate-500 text-sm">© {brand.legal.copyrightYear} {brand.legal.copyrightText}. All rights reserved.</p>
            </div>
            <div className="flex justify-center md:justify-end gap-6 text-slate-400">
              <Link to="/terms" className="hover:text-slate-600 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-slate-600 transition-colors">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Homepage wrapper - the main app content
function HomePage() {
  return (
    <>
      <MainApp />
      <LoginModal />
    </>
  );
}


// Routes component - has access to brand context
function AppRoutes() {
  const brand = useBrand();

  return (
    <Routes>
      {/* Public landing page */}
      <Route path="/" element={<ComingSoonPage />} />

      {/* Hidden test app - use this URL for testing */}
      <Route path="/studio-x7k9m2" element={<HomePage />} />

      {/* Product page for sharing/direct links */}
      <Route path="/products/:productId" element={<ProductPage />} />

      {/* Creator shop page - only available when shop feature is enabled */}
      {brand?.features?.shop?.enabled !== false && (
        <Route path="/shop/:userId" element={<ShopPage />} />
      )}

      {/* Proxy link redirects for referral tracking */}
      <Route path="/p/:code" element={<ProxyRedirect />} />

      {/* Order pages */}
      <Route path="/order-success" element={<OrderSuccessPage />} />
      <Route path="/checkout-cancelled" element={<OrderCancelledPage />} />

      {/* Legal pages */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/faq" element={<FAQ />} />

      {/* User profile - protected route */}
      <Route path="/profile" element={<ProfilePage />} />

      {/* Pricing page */}
      <Route path="/pricing" element={<PricingPage />} />

      {/* Subscription success page */}
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />

      {/* Admin dashboard - protected route (obscured URL) */}
      <Route path="/ctrl-z9k4m7" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
    </Routes>
  );
}

// Main App component with routing
function App() {
  return (
    <BrowserRouter>
      <BrandProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrandProvider>
    </BrowserRouter>
  );
}

export default App;
